import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MessageCircle, MapPin, Plus, Trash2, FileText, PhoneCall, MailOpen, Users, Paperclip, Upload, Download, Repeat, Pause, Play, Pencil } from 'lucide-react';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import StatusDot from '../components/StatusDot';
import { api, formatCurrency, API_BASE } from '../lib/api';
import { SERVICE_OPTIONS, DEFAULT_PAYMENT_TERMS_DAYS } from '../lib/services';

const NOTE_ICONS = {
  note: FileText,
  call: PhoneCall,
  email: MailOpen,
  meeting: Users,
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [tagInput, setTagInput] = useState('');
  const [tagsLocal, setTagsLocal] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attLabel, setAttLabel] = useState('');
  const [attUploading, setAttUploading] = useState(false);
  // Retainers
  const [retainers, setRetainers] = useState([]);
  const [showRetainerModal, setShowRetainerModal] = useState(false);
  const [editingRetainer, setEditingRetainer] = useState(null);
  // Transaction ledger
  const [transactions, setTransactions] = useState([]);
  const [showTxnModal, setShowTxnModal] = useState(false);
  const todayIso = new Date().toISOString().split('T')[0];
  const emptyTxn = { date: todayIso, type: 'payment', amount: '', mode: '', reference: '', notes: '' };
  const [txnForm, setTxnForm] = useState(emptyTxn);
  const [txnBusy, setTxnBusy] = useState(false);
  // Historical invoice
  const [showHistModal, setShowHistModal] = useState(false);
  const emptyHist = { invoice_no: '', date: todayIso, due_date: todayIso, service: '', amount: '', gst_percent: 18, tds_percent: 0, status: 'paid', payment_date: todayIso, notes: '' };
  const [histForm, setHistForm] = useState(emptyHist);
  const [histBusy, setHistBusy] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const emptyRetainer = {
    name: '',
    company_id: '',
    client_id: '',
    frequency: 'monthly',
    next_due_date: today,
    active: 'true',
    service: '',
    period_template: 'Monthly Retainer',
    items: [{ desc: 'Monthly Retainer', qty: 1, rate: 0, amt: 0 }],
    gst_percent: 18,
    tds_percent: 0,
    notes: '',
    payment_terms_days: DEFAULT_PAYMENT_TERMS_DAYS,
  };
  const [retainerForm, setRetainerForm] = useState(emptyRetainer);
  const [retainerServiceMode, setRetainerServiceMode] = useState('preset');
  const [retainerBusy, setRetainerBusy] = useState(false);

  const fetchAll = async () => {
    const [s, n, a, r, t] = await Promise.all([
      api.get(`/clients/${id}/summary`),
      api.get(`/clients/${id}/notes`),
      api.get(`/clients/${id}/attachments`),
      api.get(`/recurring-invoices`, { params: { client_id: id } }),
      api.get(`/clients/${id}/transactions`),
    ]);
    setSummary(s.data);
    setNotes(n.data);
    setTagsLocal(s.data.client.tags || []);
    setAttachments(a.data);
    setRetainers(r.data);
    setTransactions(t.data);
  };

  useEffect(() => { queueMicrotask(() => fetchAll()); }, [id]);

  const uploadAttachment = async (file) => {
    if (!file) return;
    setAttUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (attLabel) fd.append('label', attLabel);
      await api.post(`/clients/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttLabel('');
      const r = await api.get(`/clients/${id}/attachments`);
      setAttachments(r.data);
    } finally { setAttUploading(false); }
  };

  const removeAttachment = async (aId) => {
    if (!window.confirm('Delete this attachment?')) return;
    await api.delete(`/attachments/${aId}`);
    setAttachments(attachments.filter(a => a.id !== aId));
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    await api.post(`/clients/${id}/notes`, { content: newNote, note_type: noteType });
    setNewNote('');
    const res = await api.get(`/clients/${id}/notes`);
    setNotes(res.data);
  };

  const deleteNote = async (noteId) => {
    await api.delete(`/clients/${id}/notes/${noteId}`);
    setNotes(notes.filter(n => n.id !== noteId));
  };

  const saveTags = async (newTags) => {
    setTagsLocal(newTags);
    await api.put(`/clients/${id}`, { ...summary.client, tags: newTags });
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || tagsLocal.includes(t)) return;
    saveTags([...tagsLocal, t]);
    setTagInput('');
  };

  const removeTag = (tag) => saveTags(tagsLocal.filter(t => t !== tag));

  // ---- Retainer handlers ----
  const openNewRetainer = () => {
    setEditingRetainer(null);
    setRetainerForm({
      ...emptyRetainer,
      client_id: id,
      company_id: summary?.client?.company_id || '',
      name: summary?.client?.name ? `${summary.client.name} - Monthly Retainer` : '',
    });
    setRetainerServiceMode('preset');
    setShowRetainerModal(true);
  };
  const openEditRetainer = (r) => {
    setEditingRetainer(r);
    setRetainerForm({
      ...r,
      next_due_date: typeof r.next_due_date === 'string' ? r.next_due_date : new Date(r.next_due_date).toISOString().split('T')[0],
      gst_percent: parseFloat(r.gst_percent),
      tds_percent: parseFloat(r.tds_percent),
      payment_terms_days: parseInt(r.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
    });
    setRetainerServiceMode(r.service && !SERVICE_OPTIONS.includes(r.service) ? 'custom' : 'preset');
    setShowRetainerModal(true);
  };
  const saveRetainer = async (e) => {
    e.preventDefault();
    setRetainerBusy(true);
    try {
      const items = retainerForm.items.map(it => ({ ...it, qty: parseFloat(it.qty) || 1, rate: parseFloat(it.rate) || 0, amt: parseFloat(it.amt) || 0 }));
      const payload = {
        ...retainerForm,
        items,
        gst_percent: parseFloat(retainerForm.gst_percent) || 0,
        tds_percent: parseFloat(retainerForm.tds_percent) || 0,
        payment_terms_days: parseInt(retainerForm.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
      };
      if (editingRetainer) await api.put(`/recurring-invoices/${editingRetainer.id}`, payload);
      else await api.post('/recurring-invoices', payload);
      setShowRetainerModal(false);
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRetainerBusy(false);
    }
  };
  const deleteRetainer = async (r) => {
    if (!window.confirm(`Delete retainer "${r.name}"?`)) return;
    await api.delete(`/recurring-invoices/${r.id}`);
    fetchAll();
  };
  const toggleRetainerPause = async (r) => {
    const newActive = r.active === 'true' ? 'false' : 'true';
    await api.put(`/recurring-invoices/${r.id}`, {
      ...r,
      active: newActive,
      gst_percent: parseFloat(r.gst_percent),
      tds_percent: parseFloat(r.tds_percent),
      payment_terms_days: parseInt(r.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
    });
    fetchAll();
  };

  // Auto-recalc retainer line item amt when qty/rate change
  const updateRetainerItem = (idx, field, value) => {
    const newItems = [...retainerForm.items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    if (field === 'qty' || field === 'rate') {
      const q = parseFloat(newItems[idx].qty) || 0;
      const r = parseFloat(newItems[idx].rate) || 0;
      newItems[idx].amt = q * r;
    }
    setRetainerForm({ ...retainerForm, items: newItems });
  };
  const retainerSubtotal = retainerForm.items.reduce((s, i) => s + (parseFloat(i.amt) || 0), 0);
  const retainerTotal = retainerSubtotal + retainerSubtotal * (parseFloat(retainerForm.gst_percent) || 0) / 100 - retainerSubtotal * (parseFloat(retainerForm.tds_percent) || 0) / 100;

  // ---- Transaction handlers ----
  const openNewTxn = () => { setTxnForm(emptyTxn); setShowTxnModal(true); };
  const saveTxn = async (e) => {
    e.preventDefault();
    setTxnBusy(true);
    try {
      await api.post(`/clients/${id}/transactions`, { ...txnForm, amount: parseFloat(txnForm.amount) || 0 });
      setShowTxnModal(false);
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setTxnBusy(false); }
  };
  const deleteTxn = async (t) => {
    if (!window.confirm(`Delete transaction ₹${t.amount} dated ${t.date}?`)) return;
    await api.delete(`/transactions/${t.id}`);
    fetchAll();
  };

  // ---- Historical invoice handler ----
  const openNewHistorical = () => {
    setHistForm({
      ...emptyHist,
      invoice_no: '',
      service: 'Marketing',
      date: todayIso,
      due_date: todayIso,
      payment_date: todayIso,
    });
    setShowHistModal(true);
  };
  const saveHistorical = async (e) => {
    e.preventDefault();
    setHistBusy(true);
    try {
      const amount = parseFloat(histForm.amount) || 0;
      // Create historical invoice
      const payload = {
        invoice_no: histForm.invoice_no,
        date: histForm.date,
        due_date: histForm.due_date || histForm.date,
        company_id: summary.client.company_id,
        client_id: id,
        service: histForm.service || null,
        period: null,
        items: [{ desc: histForm.service || 'Historical bill', qty: 1, rate: amount, amt: amount }],
        gst_percent: parseFloat(histForm.gst_percent) || 0,
        tds_percent: parseFloat(histForm.tds_percent) || 0,
        notes: histForm.notes || null,
        status: histForm.status,
        source: 'historical',
      };
      const inv = await api.post('/invoices', payload);
      // If marked paid, also create a Payment row
      if (histForm.status === 'paid' && amount > 0) {
        const banks = await api.get('/bank-accounts');
        const bank = banks.data.find(b => b.company_id === summary.client.company_id) || banks.data[0];
        if (bank) {
          const total = amount + amount * (parseFloat(histForm.gst_percent) || 0) / 100 - amount * (parseFloat(histForm.tds_percent) || 0) / 100;
          await api.post('/payments', {
            invoice_id: inv.data.id,
            amount: total,
            date: histForm.payment_date || histForm.date,
            bank_account_id: bank.id,
            mode: 'bank',
            reference: 'Historical',
            notes: 'Auto-logged with historical invoice',
          });
        }
      }
      setShowHistModal(false);
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setHistBusy(false); }
  };

  if (!summary) {
    return <Layout title="Client"><div className="text-center py-8 font-body text-text-secondary">Loading...</div></Layout>;
  }

  const c = summary.client;
  const todayDate = new Date();

  // Age buckets for this client
  const buckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  summary.invoices.forEach(inv => {
    if (inv.balance <= 0 || !inv.due_date) return;
    const days = Math.floor((todayDate - new Date(inv.due_date)) / 86400000);
    if (days <= 30) buckets['0-30'] += inv.balance;
    else if (days <= 60) buckets['31-60'] += inv.balance;
    else if (days <= 90) buckets['61-90'] += inv.balance;
    else buckets['90+'] += inv.balance;
  });

  const collectionRate = summary.total_invoiced > 0 ? Math.round((summary.total_paid / summary.total_invoiced) * 100) : 0;

  return (
    <Layout title={c.name}>
      <div className="flex items-center justify-between mb-4">
        <button data-testid="back-to-clients" onClick={() => navigate('/clients')} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover flex items-center transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1} /> Back to Clients
        </button>
        <button
          data-testid="new-invoice-from-client"
          onClick={() => navigate(`/invoices/new?client_id=${c.id}`)}
          className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading text-sm flex items-center transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New Invoice
        </button>
      </div>

      {/* Top hero strip */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
        <div data-testid="client-stat-invoiced" className="stat-card bg-surface border border-border p-6 fade-up">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Invoiced</p>
          <p className="font-numbers text-2xl text-text-primary tabular-nums">{formatCurrency(summary.total_invoiced)}</p>
          <p className="font-body text-xs text-text-secondary mt-2 mono-num">{summary.invoice_count} invoices</p>
        </div>
        <div data-testid="client-stat-paid" className="stat-card bg-surface border border-border p-6 fade-up fade-up-d1">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Collected</p>
          <p className="font-numbers text-2xl text-success tabular-nums">{formatCurrency(summary.total_paid)}</p>
          <p className="font-body text-xs text-text-secondary mt-2 mono-num">{collectionRate}% collection rate</p>
        </div>
        <div data-testid="client-stat-outstanding" className="stat-card bg-surface border border-border p-6 border-l-2 border-l-maroon fade-up fade-up-d2">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Outstanding</p>
          <p className="font-numbers text-2xl text-maroon tabular-nums">{formatCurrency(Math.max(0, summary.outstanding))}</p>
          <p className="font-body text-xs text-text-secondary mt-2 mono-num">Balance due</p>
        </div>
        <div data-testid="client-stat-age" className="stat-card bg-surface border border-border p-6 fade-up fade-up-d3">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-3">Aging</p>
          <div className="space-y-1.5">
            {Object.entries(buckets).map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs">
                <span className="font-body text-text-secondary mono-num">{k} days</span>
                <span className={`font-numbers tabular-nums ${v > 0 ? 'text-maroon' : 'text-text-secondary'}`}>{formatCurrency(v)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Profile + Quick actions */}
        <div className="bg-surface border border-border p-6 fade-up">
          <h3 className="font-heading text-xl font-light text-text-primary mb-6">Profile</h3>
          
          {/* Quick actions */}
          <div className="grid grid-cols-3 gap-2 mb-6 pb-6 border-b border-border">
            {c.phone && (
              <a data-testid="quick-call" href={`tel:${c.phone}`} className="flex flex-col items-center py-3 border border-border hover:border-maroon hover:text-maroon transition-colors">
                <Phone className="w-4 h-4 mb-1" strokeWidth={1} />
                <span className="font-body text-xs uppercase tracking-widest">Call</span>
              </a>
            )}
            {c.email && (
              <a data-testid="quick-email" href={`mailto:${c.email}`} className="flex flex-col items-center py-3 border border-border hover:border-maroon hover:text-maroon transition-colors">
                <Mail className="w-4 h-4 mb-1" strokeWidth={1} />
                <span className="font-body text-xs uppercase tracking-widest">Email</span>
              </a>
            )}
            {c.phone && (
              <a data-testid="quick-whatsapp" href={`https://wa.me/${c.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex flex-col items-center py-3 border border-border hover:border-maroon hover:text-maroon transition-colors">
                <MessageCircle className="w-4 h-4 mb-1" strokeWidth={1} />
                <span className="font-body text-xs uppercase tracking-widest">WhatsApp</span>
              </a>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Contact</p>
              <p className="font-body text-sm text-text-primary">{c.contact_person || '—'}</p>
              <p className="font-body text-sm text-text-secondary mono-num">{c.phone || '—'}</p>
              <p className="font-body text-sm text-text-secondary">{c.email || '—'}</p>
            </div>
            <div className="pt-4 border-t border-border">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">Type</p>
              <p className="font-body text-sm text-text-primary">{c.client_type || '—'}</p>
            </div>
            {c.address && (
              <div className="pt-4 border-t border-border">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1 flex items-center"><MapPin className="w-3 h-3 mr-1" strokeWidth={1} /> Address</p>
                <p className="font-body text-sm text-text-primary">{c.address}</p>
              </div>
            )}
            {c.gstin && (
              <div className="pt-4 border-t border-border">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">GSTIN / PAN</p>
                <p className="font-body text-sm text-text-primary mono-num">{c.gstin}</p>
                <p className="font-body text-sm text-text-secondary mono-num">{c.pan || '—'}</p>
              </div>
            )}
            
            {/* Tags */}
            <div className="pt-4 border-t border-border">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Tags</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {tagsLocal.map(tag => (
                  <span key={tag} data-testid={`tag-${tag}`} className="inline-flex items-center px-2 py-1 bg-gold/10 border border-gold/30 text-xs font-body uppercase tracking-widest text-gold">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="ml-2 hover:text-maroon"><Trash2 className="w-3 h-3" strokeWidth={1} /></button>
                  </span>
                ))}
                {tagsLocal.length === 0 && <span className="font-body text-xs text-text-secondary">No tags</span>}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="tag-input"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag..."
                  className="flex-1 bg-transparent border border-border focus:border-maroon outline-none px-2 py-1 font-body text-xs transition-colors"
                />
                <button data-testid="add-tag-button" onClick={addTag} type="button" className="text-maroon hover:text-maroon-hover text-xs uppercase tracking-widest font-body">Add</button>
              </div>
            </div>
          </div>
        </div>

        {/* Notes timeline */}
        <div className="lg:col-span-2 bg-surface border border-border p-6 fade-up fade-up-d1">
          <h3 className="font-heading text-xl font-light text-text-primary mb-4">Activity & Notes</h3>
          
          <form onSubmit={addNote} className="mb-6 pb-6 border-b border-border">
            <div className="flex gap-2 mb-2">
              {['note', 'call', 'email', 'meeting'].map(t => {
                const Icon = NOTE_ICONS[t];
                return (
                  <button
                    key={t}
                    data-testid={`note-type-${t}`}
                    type="button"
                    onClick={() => setNoteType(t)}
                    className={`flex items-center px-3 py-1.5 border text-xs uppercase tracking-widest font-body transition-colors ${noteType === t ? 'border-maroon text-maroon bg-cream' : 'border-border text-text-secondary hover:border-maroon hover:text-maroon'}`}
                  >
                    <Icon className="w-3 h-3 mr-1.5" strokeWidth={1} />
                    {t}
                  </button>
                );
              })}
            </div>
            <textarea
              data-testid="note-content-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Log a note, call summary, meeting outcome..."
              rows={2}
              className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors mb-2"
            />
            <button data-testid="add-note-button" type="submit" disabled={!newNote.trim()} className="bg-maroon text-white hover:bg-maroon-hover px-4 py-2 font-heading text-sm transition-colors disabled:opacity-30">
              <Plus className="w-4 h-4 inline mr-1" strokeWidth={1} /> Add Entry
            </button>
          </form>

          <div className="space-y-4">
            {notes.length === 0 && (
              <p className="text-center py-6 font-body text-sm text-text-secondary">No notes yet · Start logging activity above</p>
            )}
            {notes.map((n, i) => {
              const Icon = NOTE_ICONS[n.note_type] || FileText;
              return (
                <div key={n.id} data-testid={`note-${n.id}`} className="flex gap-3 pb-4 border-b border-border last:border-0 group">
                  <div className="flex-shrink-0 w-8 h-8 border border-border flex items-center justify-center bg-cream">
                    <Icon className="w-4 h-4 text-maroon" strokeWidth={1} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">{n.note_type}</p>
                      <div className="flex items-center gap-2">
                        <span className="font-body text-xs text-text-secondary mono-num">{new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        <button onClick={() => deleteNote(n.id)} className="text-text-secondary hover:text-maroon opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3 h-3" strokeWidth={1} />
                        </button>
                      </div>
                    </div>
                    <p className="font-body text-sm text-text-primary whitespace-pre-wrap break-words">{n.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Invoice history */}
      <div className="bg-surface border border-border p-6 fade-up fade-up-d2">
        <h3 className="font-heading text-xl font-light text-text-primary mb-4">Invoice History · {summary.invoice_count}</h3>
        {summary.invoices.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Total</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Balance</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.invoices.map(inv => (
                <tr key={inv.id} className="border-b border-border hover:bg-cream cursor-pointer transition-colors" onClick={() => navigate(`/invoices/${inv.id}`)}>
                  <td className="py-3 font-body text-sm text-text-primary mono-num">{inv.invoice_no}</td>
                  <td className="py-3 font-body text-sm text-text-secondary mono-num">{inv.date}</td>
                  <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(inv.total)}</td>
                  <td className="py-3 font-numbers text-sm text-maroon text-right tabular-nums">{formatCurrency(inv.balance)}</td>
                  <td className="py-3"><span className="inline-flex items-center"><StatusDot status={inv.status} pulse={inv.status === 'overdue'} /><StatusBadge status={inv.status} /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center py-8 font-body text-sm text-text-secondary">No invoices yet</p>
        )}
      </div>

      {/* Retainers */}
      <div data-testid="client-retainers" className="bg-surface border border-border p-6 mt-6 fade-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-light text-text-primary flex items-center">
            <Repeat className="w-4 h-4 mr-2 text-maroon" strokeWidth={1.5} /> Retainers · {retainers.length}
          </h3>
          <button
            data-testid="new-retainer-from-client"
            onClick={openNewRetainer}
            className="bg-maroon text-white hover:bg-maroon-hover px-4 py-2 font-heading text-sm flex items-center transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Retainer
          </button>
        </div>

        {retainers.length === 0 ? (
          <p className="text-center py-6 font-body text-sm text-text-secondary">No retainers — set one up to auto-generate monthly invoices.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Name</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Frequency</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Next Due</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Total / cycle</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Status</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {retainers.map(r => {
                const sub = (r.items || []).reduce((s, i) => s + (parseFloat(i.amt) || 0), 0);
                const tot = sub + sub * (parseFloat(r.gst_percent) || 0) / 100 - sub * (parseFloat(r.tds_percent) || 0) / 100;
                const isPaused = r.active === 'false';
                return (
                  <tr key={r.id} data-testid={`retainer-row-${r.id}`} className={`border-b border-border hover:bg-cream transition-colors ${isPaused ? 'opacity-60' : ''}`}>
                    <td className="py-3 font-body text-sm text-text-primary">{r.name}</td>
                    <td className="py-3 font-body text-sm text-text-secondary">{r.frequency}</td>
                    <td className="py-3 font-body text-sm text-text-secondary mono-num">{r.next_due_date}</td>
                    <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(tot)}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 text-xs uppercase tracking-widest font-body border ${isPaused ? 'bg-gold/10 text-gold border-gold/40' : 'bg-success/10 text-success border-success/30'}`}>{isPaused ? 'Paused' : 'Active'}</span>
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <button data-testid={`toggle-retainer-${r.id}`} onClick={() => toggleRetainerPause(r)} className="text-text-secondary hover:text-maroon mr-3" title={isPaused ? 'Resume' : 'Pause'}>
                        {isPaused ? <Play className="w-4 h-4 inline" strokeWidth={1.5} /> : <Pause className="w-4 h-4 inline" strokeWidth={1.5} />}
                      </button>
                      <button data-testid={`edit-retainer-${r.id}`} onClick={() => openEditRetainer(r)} className="text-text-secondary hover:text-maroon mr-3" title="Edit">
                        <Pencil className="w-4 h-4 inline" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`delete-retainer-${r.id}`} onClick={() => deleteRetainer(r)} className="text-text-secondary hover:text-maroon" title="Delete">
                        <Trash2 className="w-4 h-4 inline" strokeWidth={1.5} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Retainer modal */}
      {showRetainerModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveRetainer} className="bg-cream border border-border max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-heading text-2xl font-light mb-6">{editingRetainer ? 'Edit Retainer' : 'New Retainer'}</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Retainer Name</label>
                <input data-testid="retainer-form-name" required value={retainerForm.name} onChange={(e) => setRetainerForm({ ...retainerForm, name: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Frequency</label>
                <select data-testid="retainer-form-frequency" value={retainerForm.frequency} onChange={(e) => setRetainerForm({ ...retainerForm, frequency: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors">
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Next Due Date</label>
                <input data-testid="retainer-form-due" required type="date" value={retainerForm.next_due_date} onChange={(e) => setRetainerForm({ ...retainerForm, next_due_date: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors" />
                <p className="font-body text-xs text-text-secondary mt-1">Bills auto-generate on this day each month.</p>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Service</label>
                <select
                  data-testid="retainer-form-service"
                  value={retainerServiceMode === 'custom' ? '__other__' : (retainerForm.service || '')}
                  onChange={(e) => {
                    if (e.target.value === '__other__') {
                      setRetainerServiceMode('custom');
                      setRetainerForm({ ...retainerForm, service: '' });
                    } else {
                      setRetainerServiceMode('preset');
                      setRetainerForm({ ...retainerForm, service: e.target.value });
                    }
                  }}
                  className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors"
                >
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="__other__">Other…</option>
                </select>
                {retainerServiceMode === 'custom' && (
                  <input value={retainerForm.service || ''} onChange={(e) => setRetainerForm({ ...retainerForm, service: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors mt-2" placeholder="Custom service name" />
                )}
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Payment Terms (days)</label>
                <input data-testid="retainer-form-terms" type="number" value={retainerForm.payment_terms_days} onChange={(e) => setRetainerForm({ ...retainerForm, payment_terms_days: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors" />
              </div>
              <div className="col-span-2">
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Line items</label>
                {retainerForm.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                    <input value={it.desc} onChange={(e) => updateRetainerItem(i, 'desc', e.target.value)} placeholder="Description" className="col-span-5 bg-transparent border border-border focus:border-maroon outline-none px-2 py-2 font-body text-sm" />
                    <input type="number" value={it.qty} onChange={(e) => updateRetainerItem(i, 'qty', e.target.value)} placeholder="Qty" className="col-span-2 bg-transparent border border-border focus:border-maroon outline-none px-2 py-2 font-body text-sm" />
                    <input type="number" value={it.rate} onChange={(e) => updateRetainerItem(i, 'rate', e.target.value)} placeholder="Rate" className="col-span-2 bg-transparent border border-border focus:border-maroon outline-none px-2 py-2 font-body text-sm" />
                    <input type="number" value={it.amt} onChange={(e) => updateRetainerItem(i, 'amt', e.target.value)} placeholder="Amount" className="col-span-2 bg-transparent border border-border focus:border-maroon outline-none px-2 py-2 font-body text-sm" />
                    <button type="button" onClick={() => setRetainerForm({ ...retainerForm, items: retainerForm.items.filter((_, idx) => idx !== i) })} className="col-span-1 text-text-secondary hover:text-maroon"><Trash2 className="w-3.5 h-3.5 inline" strokeWidth={1.5} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setRetainerForm({ ...retainerForm, items: [...retainerForm.items, { desc: '', qty: 1, rate: 0, amt: 0 }] })} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">+ Add line</button>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">GST %</label>
                <input type="number" value={retainerForm.gst_percent} onChange={(e) => setRetainerForm({ ...retainerForm, gst_percent: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">TDS %</label>
                <input type="number" value={retainerForm.tds_percent} onChange={(e) => setRetainerForm({ ...retainerForm, tds_percent: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div className="col-span-2 flex justify-between items-baseline pt-3 border-t border-border">
                <span className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Subtotal · {formatCurrency(retainerSubtotal)} · GST {retainerForm.gst_percent}% · TDS {retainerForm.tds_percent}%</span>
                <span className="font-numbers text-xl text-maroon tabular-nums">{formatCurrency(retainerTotal)}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button type="button" onClick={() => setShowRetainerModal(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading">Cancel</button>
              <button data-testid="save-retainer-button" type="submit" disabled={retainerBusy} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading disabled:opacity-50">{retainerBusy ? 'Saving…' : (editingRetainer ? 'Update' : 'Create')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Historical Invoice + Transaction Ledger */}
      <div data-testid="client-ledger" className="bg-surface border border-border p-6 mt-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-light text-text-primary flex items-center">
            <FileText className="w-4 h-4 mr-2 text-maroon" strokeWidth={1.5} /> Historical Bills & Transactions
          </h3>
          <div className="flex gap-2">
            <button data-testid="log-historical-invoice-btn" onClick={openNewHistorical} className="bg-transparent border border-border text-text-primary hover:border-maroon hover:text-maroon px-3 py-2 font-heading text-xs flex items-center transition-colors">
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Log Historical Invoice
            </button>
            <button data-testid="log-transaction-btn" onClick={openNewTxn} className="bg-maroon text-white hover:bg-maroon-hover px-3 py-2 font-heading text-xs flex items-center transition-colors">
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Log Transaction
            </button>
          </div>
        </div>
        <p className="font-body text-xs text-text-secondary mb-4">
          <strong>Historical Invoices</strong> are real bills issued before this system was set up — they DO feed into dashboards. <strong>Transactions</strong> are lightweight cash/note entries — they DON&apos;T affect dashboards.
        </p>

        {transactions.length === 0 ? (
          <p className="text-center py-4 font-body text-sm text-text-secondary">No transactions logged.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Type</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Mode</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Reference</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Notes</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className="border-b border-border hover:bg-cream transition-colors">
                  <td className="py-3 font-body text-sm text-text-secondary mono-num">{t.date}</td>
                  <td className="py-3 font-body text-sm text-text-primary capitalize">{t.type}</td>
                  <td className="py-3 font-numbers text-sm text-right tabular-nums">{formatCurrency(parseFloat(t.amount))}</td>
                  <td className="py-3 font-body text-sm text-text-secondary capitalize">{t.mode || '—'}</td>
                  <td className="py-3 font-body text-sm text-text-secondary">{t.reference || '—'}</td>
                  <td className="py-3 font-body text-sm text-text-secondary">{t.notes || '—'}</td>
                  <td className="py-3 text-right">
                    <button data-testid={`delete-txn-${t.id}`} onClick={() => deleteTxn(t)} className="text-text-secondary hover:text-maroon" title="Delete">
                      <Trash2 className="w-4 h-4 inline" strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Transaction modal */}
      {showTxnModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveTxn} className="bg-cream border border-border max-w-xl w-full p-8">
            <h2 className="font-heading text-2xl font-light mb-6">Log Transaction</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Date</label>
                <input data-testid="txn-form-date" required type="date" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Type</label>
                <select data-testid="txn-form-type" value={txnForm.type} onChange={(e) => setTxnForm({ ...txnForm, type: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm">
                  <option value="payment">Payment received</option>
                  <option value="invoice">Invoice issued</option>
                  <option value="refund">Refund</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Amount (₹)</label>
                <input data-testid="txn-form-amount" required type="number" step="0.01" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Mode</label>
                <select data-testid="txn-form-mode" value={txnForm.mode} onChange={(e) => setTxnForm({ ...txnForm, mode: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm">
                  <option value="">—</option>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="card">Card</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Reference (UPI ref, cheque no, etc.)</label>
                <input data-testid="txn-form-ref" value={txnForm.reference} onChange={(e) => setTxnForm({ ...txnForm, reference: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Notes</label>
                <textarea data-testid="txn-form-notes" rows={3} value={txnForm.notes} onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button type="button" onClick={() => setShowTxnModal(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading">Cancel</button>
              <button data-testid="save-txn-button" type="submit" disabled={txnBusy} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading disabled:opacity-50">{txnBusy ? 'Saving…' : 'Save Transaction'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Historical Invoice modal */}
      {showHistModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={saveHistorical} className="bg-cream border border-border max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-heading text-2xl font-light mb-2">Log Historical Invoice</h2>
            <p className="font-body text-xs text-text-secondary mb-6">For real bills issued before this system existed. These DO show up in dashboards & aging reports.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Invoice #</label>
                <input data-testid="hist-form-no" required value={histForm.invoice_no} onChange={(e) => setHistForm({ ...histForm, invoice_no: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" placeholder="e.g. WM/2024/045" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Service</label>
                <select data-testid="hist-form-service" value={histForm.service} onChange={(e) => setHistForm({ ...histForm, service: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm">
                  {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Invoice Date</label>
                <input data-testid="hist-form-date" required type="date" value={histForm.date} onChange={(e) => setHistForm({ ...histForm, date: e.target.value, due_date: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Due Date</label>
                <input data-testid="hist-form-due" required type="date" value={histForm.due_date} onChange={(e) => setHistForm({ ...histForm, due_date: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Amount (subtotal) ₹</label>
                <input data-testid="hist-form-amount" required type="number" step="0.01" value={histForm.amount} onChange={(e) => setHistForm({ ...histForm, amount: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Status</label>
                <select data-testid="hist-form-status" value={histForm.status} onChange={(e) => setHistForm({ ...histForm, status: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm">
                  <option value="paid">Paid (also creates a Payment row)</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">GST %</label>
                <input type="number" value={histForm.gst_percent} onChange={(e) => setHistForm({ ...histForm, gst_percent: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              <div>
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">TDS %</label>
                <input type="number" value={histForm.tds_percent} onChange={(e) => setHistForm({ ...histForm, tds_percent: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
              {histForm.status === 'paid' && (
                <div>
                  <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Payment Date</label>
                  <input data-testid="hist-form-paydate" type="date" value={histForm.payment_date} onChange={(e) => setHistForm({ ...histForm, payment_date: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
                </div>
              )}
              <div className="col-span-2">
                <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">Notes</label>
                <textarea data-testid="hist-form-notes" rows={2} value={histForm.notes} onChange={(e) => setHistForm({ ...histForm, notes: e.target.value })} className="w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button type="button" onClick={() => setShowHistModal(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading">Cancel</button>
              <button data-testid="save-hist-button" type="submit" disabled={histBusy} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading disabled:opacity-50">{histBusy ? 'Saving…' : 'Log Invoice'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Attachments */}
      <div data-testid="client-attachments" className="bg-surface border border-border p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-light text-text-primary flex items-center"><Paperclip className="w-4 h-4 mr-2 text-maroon" strokeWidth={1.5} /> Attachments</h3>
          <span className="font-body text-xs text-text-secondary">{attachments.length} file{attachments.length === 1 ? '' : 's'}</span>
        </div>

        <div className="flex gap-3 mb-5">
          <input
            data-testid="attachment-label"
            type="text"
            placeholder="Label (e.g. SOW, Contract)"
            value={attLabel}
            onChange={(e) => setAttLabel(e.target.value)}
            className="flex-1 bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors"
          />
          <label data-testid="attachment-upload-btn" className="bg-maroon text-white hover:bg-maroon-hover px-4 py-2 font-heading text-sm cursor-pointer flex items-center transition-colors">
            <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> {attUploading ? 'Uploading…' : 'Upload PDF / Image'}
            <input data-testid="attachment-file-input" type="file" accept="application/pdf,image/*" disabled={attUploading} onChange={(e) => uploadAttachment(e.target.files[0])} className="hidden" />
          </label>
        </div>

        {attachments.length === 0 ? (
          <p className="text-center py-6 font-body text-sm text-text-secondary">No attachments yet — upload contracts, POs, SOWs as PDFs.</p>
        ) : (
          <table className="w-full">
            <tbody>
              {attachments.map(a => (
                <tr key={a.id} data-testid={`attachment-row-${a.id}`} className="border-b border-border">
                  <td className="py-3"><Paperclip className="w-3.5 h-3.5 text-text-secondary" strokeWidth={1.5} /></td>
                  <td className="py-3 font-body text-sm text-text-primary">{a.label || '—'}</td>
                  <td className="py-3 font-body text-sm text-text-secondary truncate max-w-xs">{a.filename}</td>
                  <td className="py-3 font-body text-xs text-text-secondary">{Math.round((a.size_bytes || 0) / 1024)} KB</td>
                  <td className="py-3 font-body text-xs text-text-secondary">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="py-3 text-right space-x-3">
                    <a data-testid={`attachment-download-${a.id}`} href={`${API_BASE}/files/${a.path}`} target="_blank" rel="noreferrer" className="font-heading text-xs text-maroon hover:underline inline-flex items-center"><Download className="w-3 h-3 mr-1" strokeWidth={1.5} /> Download</a>
                    <button onClick={() => removeAttachment(a.id)} className="text-text-secondary hover:text-maroon"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default ClientDetail;
